import { ServiceException } from '@inter-ikea-kompis/exceptions';
import ServiceFetch from '../../utilities/ServiceFetch.js';
import DexfAccountServiceUtility from '../authentication/account-service/DexfAccountServiceUtility.js';
const DEFAULT_CONFIGURATION_SCHEMA_VERSION = '1.0';
/**
 * This class is used for saving planner configurations to the Dexf SaveService.
 */
export default class DexfSaveService {
    /**
     * Constructor.
     *
     * @param options Options.
     * @param options.dexfApplicationId DEXF application ID.
     * @param options.endpoint Endpoint.
     * @param options.dexfApiKey DEXF API key.
     * @param [options.dexfConfigurationSchemaVersion] Configuration schema version. Defaults to "1.0".
     */
    constructor(options) {
        this.dexfIcfVersion = '1.0';
        this.dexfApplicationId = options.dexfApplicationId;
        this.endpoint = options.endpoint;
        this.dexfApiKey = options.dexfApiKey;
        this.dexfConfigurationSchemaVersion =
            options.dexfConfigurationSchemaVersion || DEFAULT_CONFIGURATION_SCHEMA_VERSION;
    }
    /**
     * Fetches and returns a saved configuration version.
     *
     * @param options ...
     */
    async getConfiguration(options) {
        const { token, id } = options;
        if (token && !DexfAccountServiceUtility.isTokenValid(token)) {
            throw new ServiceException('Invalid JWT token is provided.');
        }
        const response = await this.fetchConfiguration({ token, id });
        return this.getSaveConfigurationObject(response);
    }
    /**
     * Fetches and returns users configurations.
     *
     * @param token ...
     * @param filters IGetConfigurationsFilters.
     */
    async getConfigurations(token, filters = {}) {
        if (!DexfAccountServiceUtility.isTokenValid(token)) {
            throw new ServiceException('Invalid JWT token is provided.');
        }
        const { latest, applications } = filters;
        // Optional to decide if we only want the latest versions.
        const url = latest ? this.endpoint + '?filter.latest=' + latest : this.endpoint;
        const promises = [];
        try {
            if (applications) {
                applications.map((application) => promises.push(this.getSaveConfigurations(token, url + '?filter.appId=' + application)));
            }
            else {
                promises.push(this.getSaveConfigurations(token, url));
            }
            const configurations = await Promise.all(promises);
            return configurations.flatMap((c) => c.designs);
        }
        catch (error) {
            throw new ServiceException('Failed to fetch list of configurations from DEXF save service API.');
        }
    }
    /**
     * Saves a planner configuration.
     *
     * @param token ...
     * @param options IConfigurationOptions for saving a new configuration | IConfigurationNewVersionOptions for saving a new version of a configuration.
     */
    async saveConfiguration(token, options) {
        if (!DexfAccountServiceUtility.isTokenValid(token)) {
            throw new ServiceException('Invalid JWT token is provided.');
        }
        let response;
        let designIdConfigurationToUpdate;
        let nameConfigurationOldVersion;
        let descriptionConfigurationOldVersion;
        if ('designId' in options && options.designId) {
            // designId is given, which means intent is to save a new version of configuration
            // Fetch configuration with given designId
            const configurationToPossiblyUpdate = await this.fetchConfiguration({
                token,
                id: options.designId
            });
            // If logged in user is owner of fetched configuration, set designIdConfigurationToUpdate
            designIdConfigurationToUpdate = configurationToPossiblyUpdate.owner
                ? options.designId
                : undefined;
            nameConfigurationOldVersion = configurationToPossiblyUpdate.name;
            descriptionConfigurationOldVersion = configurationToPossiblyUpdate.description;
        }
        if (designIdConfigurationToUpdate) {
            // Update configuration = save new version
            response = await ServiceFetch.json(this.endpoint + '/ids/' + designIdConfigurationToUpdate.toUpperCase(), {
                method: 'PUT',
                body: this.getRequestBody(options),
                credentials: 'same-origin',
                headers: {
                    'dexf-api-key': this.dexfApiKey,
                    'content-type': 'application/json',
                    authorization: 'Bearer ' + token
                }
            });
        }
        else {
            // Save new configuration
            const saveNewOptions = {
                ...options,
                /*
                 * To cover the case where intent was to save a new version but logged in user is not the owner of the configuration,
                 * we use name from the old version if configurationName is not given (optional when saving new version), since name
                 * is mandatory in when saving new configuration.
                 * Note: In this case the new configuration will be saved without description if configurationDescription is not given
                 * (optional when saving new version and not in response when fetching configuration user does not own).
                 * Might be something we want to fix if it turns out to be a common case / an issue.
                 */
                configurationName: options.configurationName ?? nameConfigurationOldVersion
            };
            response = await ServiceFetch.json(this.endpoint, {
                method: 'POST',
                body: this.getRequestBody(saveNewOptions),
                credentials: 'same-origin',
                headers: {
                    'dexf-api-key': this.dexfApiKey,
                    'content-type': 'application/json',
                    authorization: 'Bearer ' + token
                }
            });
        }
        // We do transform our response so if configuration has been saved we will transform it.
        if (response?.designId) {
            // If there is only one image we will add typeCode or we will make a list with the images and links.
            const images = [];
            if (typeof options.configurationImage === 'string') {
                images.push({
                    typeCode: 'main-design-image',
                    image: options.configurationImage
                });
            }
            else if (Array.isArray(options.configurationImage)) {
                images.push(...options.configurationImage);
            }
            const imageResponse = await Promise.all(images.map((image) => this.saveImage(token, response._links, image.image, image.typeCode)));
            return {
                appId: this.dexfApplicationId,
                configuration: options.configuration,
                configurationSchemaVersion: this.dexfConfigurationSchemaVersion,
                description: options.configurationDescription ?? descriptionConfigurationOldVersion,
                designId: response.designId,
                icfSaved: response.icf_saved,
                imageUrl: imageResponse,
                info: options.versionInfo,
                name: options.configurationName ?? nameConfigurationOldVersion
            };
        }
        throw new ServiceException('Failed to save configuration to DEXF save service API. No data in response.');
    }
    /**
     * Saves image to DEXF save service.
     *
     * @param token ...
     * @param links ...
     * @param configurationImage ...
     * @param typeCode ...
     * @returns Promise object.
     */
    async saveImage(token, links, configurationImage, typeCode) {
        try {
            const options = {
                method: 'POST',
                body: configurationImage,
                credentials: 'same-origin',
                headers: {
                    'dexf-api-key': this.dexfApiKey,
                    'content-type': 'text/plain;charset=UTF-8',
                    authorization: 'Bearer ' + token
                }
            };
            const response = await ServiceFetch.json(links[typeCode].href, options);
            return {
                typeCode,
                url: response.url
            };
        }
        catch (error) {
            throw new ServiceException('Failed to save image. Error: ' + error);
        }
    }
    /**
     * Returns payload to be used when saving a configuration (new configuration or new version).
     *
     * @param options ...
     */
    getRequestBody(options) {
        const body = {
            appId: this.dexfApplicationId,
            itemList: {
                items: options.shoppingItems.map((item) => {
                    return { itemType: item.type, itemNo: item.id, quantity: item.quantity };
                })
            },
            configuration: {
                version: this.dexfConfigurationSchemaVersion,
                content: options.configuration
            },
            info: options.versionInfo
        };
        if (options.configurationName) {
            Object.assign(body, {
                name: options.configurationName
            });
        }
        if (options.configurationDescription) {
            Object.assign(body, {
                description: options.configurationDescription
            });
        }
        if (options.insightsCombinationFormat) {
            Object.assign(body, {
                icf: {
                    version: this.dexfIcfVersion,
                    content: options.insightsCombinationFormat
                }
            });
        }
        return JSON.stringify(body);
    }
    getSaveConfigurationObject(response) {
        return {
            appId: response.appId.toUpperCase(),
            configuration: response.configuration.content,
            configurationSchemaVersion: response.configuration.version,
            description: response.description,
            designId: response.designId,
            icf: response.icf,
            imageUrl: response.image,
            info: response.info,
            itemList: response.itemList?.items ? this.getShoppingItemList(response.itemList) : undefined,
            latestUserVersion: response.latestUserVersion,
            name: response.name
        };
    }
    async fetchConfiguration({ token, id }) {
        const url = this.endpoint + '/ids/' + id.toUpperCase();
        const headers = token
            ? {
                'dexf-api-key': this.dexfApiKey,
                authorization: 'Bearer ' + token
            }
            : { 'dexf-api-key': this.dexfApiKey };
        const fetchOptions = {
            credentials: 'same-origin',
            headers
        };
        const response = await ServiceFetch.json(url, fetchOptions);
        if (!response?.designId) {
            throw new ServiceException('Failed to fetch from configuration from DEXF save service API. No data in response.');
        }
        return response;
    }
    async getSaveConfigurations(token, url) {
        const headers = {
            credentials: 'same-origin',
            headers: {
                'dexf-api-key': this.dexfApiKey,
                authorization: 'Bearer ' + token
            }
        };
        return await ServiceFetch.json(url, headers);
    }
    getShoppingItemList(itemList) {
        return itemList.items.map((item) => ({
            id: item.itemNo,
            type: item.itemType,
            quantity: item.quantity
        }));
    }
}
//# sourceMappingURL=DexfSaveService.js.map